import React from 'react'
import type { NudgeQueueDetail, ConversationTurn } from '../../../types/api'
import {
  formatDateTime,
  htmlToPlainText,
  initialsFromName,
  normalizeForDedup,
  stripQuotedReply,
} from '../utils'
import { UpgradePrompt } from './UpgradePrompt'

// ── Types ─────────────────────────────────────────────────────────────────────

type ChatMessage = {
  id: string
  side: 'business' | 'customer'
  subject?: string
  sequenceNumber?: number
  text: string
  ts: number
}

type ChatEvent =
  | { id: string; type: 'message'; message: ChatMessage; ts: number }
  | { id: string; type: 'recovered'; recoveredAt: string; ts: number }
  | { id: string; type: 'failed'; detail: NudgeQueueDetail; ts: number }

type TimelineRow =
  | { rowType: 'divider'; label: string; id: string }
  | { rowType: 'event'; event: ChatEvent }

// ── Helpers ───────────────────────────────────────────────────────────────────

function isBusinessRole(role: ConversationTurn['role']): boolean {
  return role === 'business' || role === 'assistant'
}

function isSubstantialOverlap(a: string, b: string): boolean {
  // Treat two business messages as the "same" if one contains the other after
  // normalization, and the shorter side has enough content to be meaningful.
  // Guards against the common case where the outbound email body wraps the
  // assistant turn text with a template header/footer.
  if (!a || !b) return false
  const shorter = a.length <= b.length ? a : b
  const longer = a.length <= b.length ? b : a
  if (shorter.length < 25) return false
  return longer.includes(shorter)
}

function chatDayLabel(ts: number): string {
  const d = new Date(ts)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  if (d.toDateString() === today.toDateString()) return 'Today'
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday'
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

function buildTimeline(details: NudgeQueueDetail[]): TimelineRow[] {
  const messages: ChatMessage[] = []

  // Pass 1 — collect every outbound email as a right-side business bubble.
  const businessKeys: string[] = []
  for (const d of details) {
    const emailText = htmlToPlainText(d.emailBody)
    if (!(d.emailSubject || emailText) || !d.sentAt) continue
    const key = normalizeForDedup(emailText || d.emailSubject || '')
    if (!key) continue
    if (businessKeys.some(existing => isSubstantialOverlap(existing, key))) continue
    businessKeys.push(key)
    messages.push({
      id: `email-${d.id}`,
      side: 'business',
      subject: d.emailSubject?.trim() || undefined,
      sequenceNumber: d.sequenceNumber,
      text: emailText,
      ts: new Date(d.sentAt).getTime(),
    })
  }

  // Pass 2 — conversation turns. Assistant/business turns overlapping an
  // outbound email are dropped (the email bubble already covers them).
  const seenCustomerKeys: string[] = []
  for (const d of details) {
    for (let i = 0; i < d.conversationHistory.length; i++) {
      const turn = d.conversationHistory[i]
      const side: 'business' | 'customer' = isBusinessRole(turn.role) ? 'business' : 'customer'
      const cleaned =
        side === 'customer'
          ? stripQuotedReply(turn.content).trim()
          : htmlToPlainText(turn.content)
      if (!cleaned) continue
      const key = normalizeForDedup(cleaned)

      if (side === 'business') {
        if (businessKeys.some(existing => isSubstantialOverlap(existing, key))) continue
        businessKeys.push(key)
      } else {
        if (seenCustomerKeys.some(existing => isSubstantialOverlap(existing, key) || existing === key)) continue
        seenCustomerKeys.push(key)
      }

      messages.push({
        id: `turn-${d.id}-${i}`,
        side,
        text: cleaned,
        ts: new Date(turn.timestamp).getTime(),
      })
    }
  }

  const events: ChatEvent[] = messages.map(m => ({
    id: m.id,
    type: 'message',
    message: m,
    ts: m.ts,
  }))

  for (const d of details) {
    if ((d.status === 'RECOVERED' || d.status === 'RETURNED') && d.recoveredAt) {
      events.push({
        id: `rec-${d.id}`,
        type: 'recovered',
        recoveredAt: d.recoveredAt,
        ts: new Date(d.recoveredAt).getTime(),
      })
    }
    if (d.status === 'FAILED') {
      events.push({
        id: `fail-${d.id}`,
        type: 'failed',
        detail: d,
        ts: new Date(d.updatedAt).getTime(),
      })
    }
  }

  events.sort((a, b) => a.ts - b.ts)

  const rows: TimelineRow[] = []
  let lastDate = ''
  for (const event of events) {
    const dateStr = new Date(event.ts).toDateString()
    if (dateStr !== lastDate) {
      lastDate = dateStr
      rows.push({ rowType: 'divider', label: chatDayLabel(event.ts), id: `div-${dateStr}` })
    }
    rows.push({ rowType: 'event', event })
  }
  return rows
}

// ── Components ────────────────────────────────────────────────────────────────

export type ChatTimelineVariant = 'email' | 'chat'

function formatBubbleTime(ts: number, variant: ChatTimelineVariant): string {
  if (variant === 'chat') {
    // WhatsApp-style: just time-of-day — day is implied by the date divider above
    return new Date(ts).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }
  return formatDateTime(new Date(ts).toISOString())
}

export function ChatTimeline({
  details,
  businessName,
  customerName,
  aiGated = false,
  variant = 'email',
}: {
  details: NudgeQueueDetail[]
  businessName: string
  customerName: string
  aiGated?: boolean
  variant?: ChatTimelineVariant
}) {
  const rows = React.useMemo(() => buildTimeline(details), [details])
  const bottomRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView()
  }, [])

  if (variant === 'chat') {
    return (
      <ChatVariantTimeline
        rows={rows}
        businessName={businessName}
        customerName={customerName}
        aiGated={aiGated}
        bottomRef={bottomRef}
      />
    )
  }

  // Email variant — legacy CSS-driven layout
  const wrapperClass = `chat-area chat-area--${variant}`

  if (rows.length === 0) {
    return (
      <div className={wrapperClass} style={{ alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 13, color: 'var(--text2)' }}>No messages yet.</span>
      </div>
    )
  }

  return (
    <div className={wrapperClass}>
      {rows.map(row => {
        if (row.rowType === 'divider') {
          return (
            <div key={row.id} className="chat-day">
              <span className="chat-day-label">{row.label}</span>
            </div>
          )
        }
        const { event } = row
        if (event.type === 'message') {
          return (
            <ChatBubble
              key={event.id}
              message={event.message}
              businessName={businessName}
              customerName={customerName}
              variant={variant}
            />
          )
        }
        if (event.type === 'recovered') {
          return (
            <div key={event.id} className="chat-status-row">
              <div className="chat-status-pill recovered">
                <svg
                  width="12" height="12" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Customer recovered · {formatDateTime(event.recoveredAt)}
              </div>
            </div>
          )
        }
        if (event.type === 'failed') {
          return (
            <div key={event.id} className="chat-status-row">
              <div className="chat-status-pill failed">
                <svg
                  width="12" height="12" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                Nudge failed
                {event.detail.failedStage ? ` · ${event.detail.failedStage}` : ''}
              </div>
            </div>
          )
        }
        return null
      })}

      {aiGated ? (
        <div style={{ marginTop: 8 }}>
          <UpgradePrompt
            title="Conversation-Aware AI"
            blurb="Customer replies are read but not auto-handled on Free. Upgrade to enable AI reply management."
          />
        </div>
      ) : null}

      <div ref={bottomRef} />
    </div>
  )
}

function ChatBubble({
  message,
  businessName,
  customerName,
  variant,
}: {
  message: ChatMessage
  businessName: string
  customerName: string
  variant: ChatTimelineVariant
}) {
  const isBusiness = message.side === 'business'
  const label = isBusiness ? businessName : customerName

  return (
    <div className={`chat-row ${message.side}`}>
      <div className="chat-bubble-wrap">
        <div className="chat-sender-label">
          {label}
          {isBusiness && message.sequenceNumber && message.sequenceNumber > 1 ? (
            <span className="chat-seq-tag">Nudge #{message.sequenceNumber}</span>
          ) : null}
        </div>
        <div className={`chat-bubble ${message.side}`}>
          {message.subject ? (
            <div className="chat-bubble-subject">{message.subject}</div>
          ) : null}
          <div className="chat-bubble-body">{message.text}</div>
        </div>
        <div className="chat-ts">
          {formatBubbleTime(message.ts, variant)}
        </div>
      </div>
    </div>
  )
}

// ── WhatsApp-style chat variant (Tailwind, used by Messages tab) ─────────────

function ChatVariantTimeline({
  rows,
  businessName,
  customerName,
  aiGated,
  bottomRef,
}: {
  rows: TimelineRow[]
  businessName: string
  customerName: string
  aiGated: boolean
  bottomRef: React.RefObject<HTMLDivElement>
}) {
  const customerInitials = initialsFromName(customerName) || '··'

  if (rows.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#fafaf7] dark:bg-[#0e0e0e] text-[13px] text-gray-500 dark:text-gray-400">
        No messages yet.
      </div>
    )
  }

  // Pre-compute "is first of run" so we only render the avatar / sender label
  // on the first message of each consecutive same-side run.
  const messageEvents = rows
    .filter(r => r.rowType === 'event' && r.event.type === 'message')
    .map(r => (r.rowType === 'event' ? r.event : null))

  const firstOfRun = new Set<string>()
  for (let i = 0; i < messageEvents.length; i++) {
    const ev = messageEvents[i]
    const prev = i > 0 ? messageEvents[i - 1] : null
    if (!ev || ev.type !== 'message') continue
    if (!prev || prev.type !== 'message' || prev.message.side !== ev.message.side) {
      firstOfRun.add(ev.id)
    }
  }

  return (
    <div className="chat-wallpaper flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-1.5">
      {rows.map(row => {
        if (row.rowType === 'divider') {
          return (
            <div key={row.id} className="flex items-center justify-center py-2">
              <span className="inline-flex items-center px-3 py-[3px] rounded-full bg-white/90 dark:bg-neutral-900/80 backdrop-blur-sm border border-black/[0.06] dark:border-white/10 text-[10.5px] font-mono uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400 shadow-[0_1px_2px_rgba(16,24,40,0.04)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.4)]">
                {row.label}
              </span>
            </div>
          )
        }
        const { event } = row
        if (event.type === 'message') {
          return (
            <ChatVariantBubble
              key={event.id}
              message={event.message}
              businessName={businessName}
              customerName={customerName}
              customerInitials={customerInitials}
              showHead={firstOfRun.has(event.id)}
            />
          )
        }
        if (event.type === 'recovered') {
          return (
            <div key={event.id} className="flex justify-center py-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200/60 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-[11.5px] font-mono">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Customer recovered · {formatDateTime(event.recoveredAt)}
              </span>
            </div>
          )
        }
        if (event.type === 'failed') {
          return (
            <div key={event.id} className="flex justify-center py-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 dark:bg-red-500/10 border border-red-200/60 dark:border-red-500/30 text-red-700 dark:text-red-300 text-[11.5px] font-mono">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                Nudge failed
                {event.detail.failedStage ? ` · ${event.detail.failedStage}` : ''}
              </span>
            </div>
          )
        }
        return null
      })}

      {aiGated ? (
        <div className="mt-2">
          <UpgradePrompt
            title="Conversation-Aware AI"
            blurb="Customer replies are read but not auto-handled on Free. Upgrade to enable AI reply management."
          />
        </div>
      ) : null}

      <div ref={bottomRef} />
    </div>
  )
}

function ChatVariantBubble({
  message,
  businessName,
  customerName,
  customerInitials,
  showHead,
}: {
  message: ChatMessage
  businessName: string
  customerName: string
  customerInitials: string
  showHead: boolean
}) {
  const isBusiness = message.side === 'business'
  const timeLabel = new Date(message.ts).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

  if (isBusiness) {
    return (
      <div className={`flex justify-end ${showHead ? 'mt-3' : 'mt-0.5'}`}>
        <div className="max-w-[min(520px,72%)] flex flex-col items-end">
          {showHead && (
            <div className="flex items-center gap-1.5 mb-1 pr-1">
              <span className="text-[10.5px] font-mono uppercase tracking-[0.08em] text-[#e05a2b] font-semibold">
                {businessName}
              </span>
              {message.sequenceNumber && message.sequenceNumber > 1 ? (
                <span className="text-[9.5px] font-mono font-semibold px-1.5 py-[1px] rounded bg-[#e05a2b]/10 border border-[#e05a2b]/25 text-[#e05a2b] uppercase tracking-[0.06em]">
                  Nudge #{message.sequenceNumber}
                </span>
              ) : null}
            </div>
          )}
          <div className="relative rounded-2xl rounded-tr-md px-3.5 py-2.5 pb-1.5 text-white text-[13.5px] leading-snug shadow-[0_2px_8px_rgba(224,90,43,0.18)] bg-gradient-to-br from-[#e76434] to-[#c44a1e]">
            {message.subject ? (
              <div className="font-bold text-[13px] mb-1.5 pb-1.5 border-b border-white/25 tracking-tight">
                {message.subject}
              </div>
            ) : null}
            <div className="whitespace-pre-wrap pr-12">{message.text}</div>
            <div className="absolute bottom-1 right-2.5 flex items-center gap-1 text-[9.5px] font-mono text-white/80">
              {timeLabel}
              <svg width="13" height="9" viewBox="0 0 16 11" fill="none" aria-hidden="true">
                <path d="M1 5.5L4.5 9L10 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6 5.5L9.5 9L15 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Customer (inbound) — left side, avatar
  return (
    <div className={`flex justify-start gap-2 ${showHead ? 'mt-3' : 'mt-0.5 pl-9'}`}>
      {showHead ? (
        <div
          className="w-7 h-7 rounded-full bg-teal-500/15 dark:bg-teal-500/20 text-teal-700 dark:text-teal-300 flex items-center justify-center text-[10.5px] font-bold font-mono shrink-0 mt-auto"
          title={customerName}
        >
          {customerInitials}
        </div>
      ) : null}
      <div className="max-w-[min(520px,72%)] flex flex-col items-start">
        {showHead && (
          <div className="text-[10.5px] font-mono uppercase tracking-[0.08em] text-teal-700 dark:text-teal-300 font-semibold mb-1 pl-1">
            {customerName}
          </div>
        )}
        <div className="relative rounded-2xl rounded-tl-md px-3.5 py-2.5 pb-1.5 bg-white dark:bg-neutral-900 text-gray-900 dark:text-gray-100 text-[13.5px] leading-snug border border-black/[0.06] dark:border-white/10 shadow-[0_1px_2px_rgba(16,24,40,0.05)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.4)]">
          {message.subject ? (
            <div className="font-bold text-[13px] mb-1.5 pb-1.5 border-b border-black/[0.08] dark:border-white/10 tracking-tight">
              {message.subject}
            </div>
          ) : null}
          <div className="whitespace-pre-wrap pr-10">{message.text}</div>
          <div className="absolute bottom-1 right-2.5 text-[9.5px] font-mono text-gray-400 dark:text-gray-500">
            {timeLabel}
          </div>
        </div>
      </div>
    </div>
  )
}
